import {useEffect,useId,useRef,useState} from 'react';
import {ChevronRight,RefreshCw,Trash2,X} from 'lucide-react';
import type {FileRecord,WorkspaceStorageUsage} from '@reados/contracts';
import {formatStorageBytes,storageStatus} from '../storage';
import {errorMessage} from '../api';
import '../storage.css';

export interface StorageUsageProps {
  usage:WorkspaceStorageUsage|null;
  loading:boolean;
  error:string;
  onRefresh:()=>Promise<unknown>;
}

export function StorageUsage({usage,loading,error,onRefresh,onOpen}:StorageUsageProps&{onOpen:()=>void}) {
  const status=usage?storageStatus(usage):null;
  return <div className={`storage-footer ${error?'storage-unavailable':status?.level||''}`}>
    <button className="storage-summary" onClick={onOpen} aria-label="工作区空间，查看存储详情">
      <span className="storage-summary-line"><span>工作区空间</span><ChevronRight size={13}/></span>
      <span className="storage-summary-value">{usage?`${formatStorageBytes(usage.usedBytes)} / ${formatStorageBytes(usage.limitBytes)}`:loading?'正在读取…':'暂时无法读取'}</span>
      {usage&&<><progress value={usage.usedBytes} max={usage.limitBytes} aria-label="工作区空间用量"/><span className="storage-summary-status">{error?`更新失败 · 上次 ${new Date(usage.measuredAt).toLocaleTimeString('zh-CN')}`:status?.message}</span></>}
    </button>
    {error&&<button className="storage-retry" disabled={loading} onClick={()=>void onRefresh().catch(()=>{})}>{loading?'正在重试…':'重试'}</button>}
  </div>;
}

/** Uses the same explicit confirmation and wording as library deletion. */
function DeleteConfirmation({item,busy,error,onCancel,onConfirm}:{item:{name:string;kind:'file'|'thread'}|null;busy:boolean;error:string;onCancel:()=>void;onConfirm:()=>void}) {
  const ref=useRef<HTMLDialogElement>(null),titleId=useId();
  useEffect(()=>{
    const dialog=ref.current;
    if(!item||!dialog)return;
    const previous=document.activeElement instanceof HTMLElement?document.activeElement:null;
    const parentDialog=dialog.parentElement?.closest('dialog');
    dialog.showModal();
    return()=>{
      dialog.close();
      if(previous?.isConnected)previous.focus();
      else if(parentDialog?.isConnected)parentDialog.querySelector<HTMLButtonElement>('button[aria-label="关闭存储详情"]')?.focus();
    };
  },[item]);
  return <dialog ref={ref} className="ipad-library-delete-dialog" aria-labelledby={titleId} onCancel={event=>{event.preventDefault();if(!busy)onCancel();}}>
    <h3 id={titleId}>删除{item?.kind==='thread'?'对话':'文件'}？</h3><p>“{item?.name}”将被永久删除。</p>
    {item&&error&&<p className="storage-error" role="alert">{error}</p>}
    <div><button autoFocus disabled={busy} onClick={onCancel}>取消</button><button className="storage-delete" disabled={busy} onClick={onConfirm}>{busy?'正在删除…':'删除'}</button></div>
  </dialog>;
}

export function StorageDialog({usage,loading,error,files,onOpenFile,onDeleteFile,onRefresh,onSubscribe,onClose}:StorageUsageProps&{files:FileRecord[];onOpenFile:(file:FileRecord)=>void;onDeleteFile:(id:string)=>Promise<void>;onSubscribe:()=>void;onClose:()=>void}) {
  const ref=useRef<HTMLDialogElement>(null),titleId=useId();
  const [remove,setRemove]=useState<FileRecord|null>(null),[deleting,setDeleting]=useState(false),[deleteError,setDeleteError]=useState('');
  const status=usage?storageStatus(usage):null;
  useEffect(()=>{
    const previous=document.activeElement instanceof HTMLElement?document.activeElement:null;
    ref.current?.showModal();
    return()=>{ref.current?.close();if(previous?.isConnected)previous.focus();};
  },[]);
  async function confirmRemove(){
    if(!remove||deleting)return;
    setDeleting(true);setDeleteError('');
    try{await onDeleteFile(remove.id);setRemove(null);}catch(value){setDeleteError(errorMessage(value));}finally{setDeleting(false);}
  }
  // Stable identity avoids reopening the nested native dialog during a refresh.
  const deleteItem=useRef<{name:string;kind:'file'}|null>(null);
  if(remove?.name!==deleteItem.current?.name)deleteItem.current=remove?{name:remove.name,kind:'file'}:null;
  return <dialog ref={ref} className="storage-dialog" aria-labelledby={titleId} onCancel={event=>{event.preventDefault();if(!remove)onClose();}}>
    <header><h2 id={titleId}>工作区空间</h2><button autoFocus aria-label="关闭存储详情" onClick={onClose}><X size={20}/></button></header>
    <div className="storage-dialog-body">
      <section className={`storage-overview ${status?.level||''}`} aria-label="空间用量">
        <div className="storage-overview-heading">
          {usage?<div className="storage-totals"><strong>{formatStorageBytes(usage.usedBytes)}</strong><span>/ {formatStorageBytes(usage.limitBytes)}</span></div>:<p role="status">{loading?'正在读取工作区空间…':'暂时无法读取工作区空间。'}</p>}
          <button type="button" className="storage-subscribe" onClick={onSubscribe}>查看订阅套餐<ChevronRight size={15}/></button>
        </div>
        {usage&&<>
        <progress value={usage.usedBytes} max={usage.limitBytes} aria-label="工作区空间用量"/>
        <p>剩余 {formatStorageBytes(usage.availableBytes)}</p>
        {status?.message&&<p className="storage-threshold" role="status">{status.message}</p>}
        <p className="storage-inodes">文件与目录数量：{usage.usedInodes.toLocaleString('zh-CN')} / {usage.limitInodes.toLocaleString('zh-CN')}</p>
        <div className="storage-measured"><span>{error?'上次读取':'更新于'} {new Date(usage.measuredAt).toLocaleTimeString('zh-CN')}</span><button disabled={loading} onClick={()=>void onRefresh().catch(()=>{})} aria-label="刷新空间用量"><RefreshCw size={14} className={loading?'spin':''}/></button></div>
        </>}
      </section>
      {error&&<div className="storage-error" role="alert"><span>{error}</span><button disabled={loading} onClick={()=>void onRefresh().catch(()=>{})}>{loading?'正在重试…':'重试'}</button></div>}
      <p className="storage-explanation">包含上传文件、AI 生成的文件及缓存。网页聊天数据库另存，不计入此空间。</p>
      <section className="storage-files" aria-label="已上传文件">
        <h3>已上传文件<span>按大小排序</span></h3>
        <p className="storage-explanation">这里只列出已上传文件，不代表工作区内的全部文件。</p>
        {[...files].sort((a,b)=>b.size-a.size||a.name.localeCompare(b.name)).map(file=><div className="storage-file" key={file.id}>
          <button className="storage-file-open" onClick={()=>{onClose();onOpenFile(file);}} title={file.name}><span>{file.name}</span><small>{formatStorageBytes(file.size)}</small></button>
          <button className="storage-file-delete" aria-label={`删除 ${file.name}`} onClick={()=>{setDeleteError('');setRemove(file);}}><Trash2 size={17}/></button>
        </div>)}
        {!files.length&&<p className="storage-explanation">暂无已上传文件。</p>}
      </section>
    </div>
    <DeleteConfirmation item={remove?deleteItem.current:null} busy={deleting} error={deleteError} onCancel={()=>setRemove(null)} onConfirm={()=>void confirmRemove()}/>
  </dialog>;
}
